import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  SelectChangeEvent
} from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { formatFileSize, formatDate } from '../utils/formatters';
import PageContainer from './common/PageContainer';
import CustomPagination from './common/CustomPagination';

interface WordFile {
  filename: string;
  size: number;
  created: string;
  templateName?: string;
}

interface WordFilesResponse {
  success: boolean;
  files: WordFile[];
  error?: string;
}

const WordFileManager: React.FC = () => {
  const [files, setFiles] = useState<WordFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [deletingFiles, setDeletingFiles] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/word-files');
      const data: WordFilesResponse = await response.json();
      
      if (data.success) {
        setFiles(data.files);
      } else {
        throw new Error(data.error || '獲取文件列表失敗');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '獲取文件列表失敗';
      setError(errorMessage);
      console.error('獲取文件列表失敗:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (filename: string) => {
    try {
      setDownloadingFiles(prev => new Set(prev).add(filename));
      
      const response = await fetch(`/api/word-files/${encodeURIComponent(filename)}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('下載失敗');
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '下載失敗';
      console.error('下載文件失敗:', err);
      alert(`下載失敗: ${errorMessage}`);
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(filename);
        return newSet;
      });
    }
  };

  const handleDelete = async (filename: string) => {
    const confirmDelete = window.confirm(`確定要刪除文件 ${filename} 嗎？`);
    
    if (confirmDelete) {
      try {
        setDeletingFiles(prev => new Set(prev).add(filename));
        
        const response = await fetch(`/api/word-files/${encodeURIComponent(filename)}`, {
          method: 'DELETE'
        });
        
        const result = await response.json();
        
        if (result.success) {
          setFiles(prev => prev.filter(file => file.filename !== filename));
        } else {
          throw new Error(result.error || '刪除失敗');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '刪除失敗';
        console.error('刪除文件失敗:', err);
        alert(`刪除失敗: ${errorMessage}`);
      } finally {
        setDeletingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(filename);
          return newSet;
        });
      }
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  if (loading) {
    return (
      <PageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            加載中...
          </Typography>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="lg">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
            Word文件列表
          </Typography>
          <Typography variant="body1" color="text.secondary">
            管理所有生成的Word文檔
          </Typography>
        </Box>
        <Tooltip title="刷新列表">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchFiles}
            sx={{ borderRadius: 2 }}
          >
            刷新
          </Button>
        </Tooltip>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchFiles}>
              重試
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {files.length === 0 ? (
        <Paper 
          sx={{ 
            p: 8, 
            textAlign: 'center',
            border: '2px dashed',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            borderRadius: 3
          }}
        >
          <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            暫無Word文件
          </Typography>
          <Typography variant="body2" color="text.secondary">
            生成第一個Word文檔吧！
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 3 }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>文件名</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>大小</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>創建時間</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>狀態</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((file, index) => {
                    const isDownloading = downloadingFiles.has(file.filename);
                    const isDeleting = deletingFiles.has(file.filename);
                    
                    return (
                      <TableRow 
                        key={index} 
                        hover
                        sx={{ 
                          '&:last-child td': { border: 0 },
                          transition: 'background-color 0.2s'
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <DescriptionIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                            <Typography variant="body2" fontWeight={500}>
                              {file.filename}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={formatFileSize(file.size)} 
                            size="small" 
                            variant="outlined"
                            sx={{ borderRadius: 2 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(file.created)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label="Word文檔" 
                            color="success" 
                            size="small"
                            sx={{ borderRadius: 2 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Tooltip title="下載">
                              <span>
                                <IconButton
                                  color="primary"
                                  onClick={() => handleDownload(file.filename)}
                                  disabled={isDownloading}
                                  size="small"
                                >
                                  {isDownloading ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <DownloadIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="刪除">
                              <span>
                                <IconButton
                                  color="error"
                                  onClick={() => handleDelete(file.filename)}
                                  disabled={isDeleting}
                                  size="small"
                                >
                                  {isDeleting ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <DeleteIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination
            count={files.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="每頁行數:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} 共 ${count} 條`}
          />
        </Paper>
      )}
    </PageContainer>
  );
};

export default WordFileManager;
